import { useMemo, useState } from 'react';
import * as RHF from 'react-hook-form';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    FormNumber,
    FormRoot,
    FormTextarea,
    Switch,
    toast,
    zodResolver,
} from '@devhop/ui';
import { Sparkles } from 'lucide-react';
import { config } from '@/shared/lib/config';
import {
    fetchLookupForInfinite,
    hydrateLookupItem,
    type LookupItem,
} from '@/shared/lib/utils';
import { FormInfiniteCombobox } from '@/shared/ui/FormInfiniteCombobox';
import type {
    CreatePromotionPathRuleDTO,
    PromotionPathRuleDetailDTO,
    UpdatePromotionPathRuleDTO,
} from '@/modules/hr/promotion/domain/types';
import { z } from 'zod';
import { promotionRuleFormSamples } from './promotionRuleFormSamples';

const PromotionRuleFormSchema = z.object({
    fromPositionId: z.string().min(1, 'ກະລຸນາເລືອກຕຳແໜ່ງເດີມ'),
    toPositionId: z.string().min(1, 'ກະລຸນາເລືອກຕຳແໜ່ງໃໝ່'),
    requiredYears: z.number().optional(),
    description: z.string(),
    isActive: z.boolean(),
});

const POSITIONS_LOOKUP_URL = `${config.apiUrl}/positions/lookup`;

const isDev =
    typeof import.meta !== 'undefined' &&
    (import.meta.hot !== undefined || import.meta.env?.MODE === 'development');

type FormValues = {
    fromPositionId: string;
    toPositionId: string;
    requiredYears: number | undefined;
    description: string;
    isActive: boolean;
};

type Props =
    | {
          mode: 'create';
          rule?: PromotionPathRuleDetailDTO | null;
          onSubmit: (values: CreatePromotionPathRuleDTO) => void;
          onCancel: () => void;
          isSubmitting?: boolean;
      }
    | {
          mode: 'edit';
          rule?: PromotionPathRuleDetailDTO | null;
          onSubmit: (values: UpdatePromotionPathRuleDTO) => void;
          onCancel: () => void;
          isSubmitting?: boolean;
      };

export function PromotionRuleForm(props: Props) {
    const isCreate = props.mode === 'create';
    const rule = props.rule;

    const defaultValues = useMemo<FormValues>(
        () => ({
            fromPositionId: rule?.fromPositionId ?? '',
            toPositionId: rule?.toPositionId ?? '',
            requiredYears: rule?.requiredYears ?? undefined,
            description: rule?.description ?? '',
            isActive: rule?.isActive ?? true,
        }),
        [rule],
    );

    const methods = RHF.useForm<FormValues>({
        // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
        resolver: zodResolver(PromotionRuleFormSchema) as any,
        defaultValues,
        values: rule
            ? {
                  fromPositionId: rule.fromPositionId,
                  toPositionId: rule.toPositionId,
                  requiredYears: rule.requiredYears ?? undefined,
                  description: rule.description ?? '',
                  isActive: rule.isActive,
              }
            : undefined,
    });
    const [sampleIndex, setSampleIndex] = useState(0);

    const handleAutoFill = () => {
        const sample = promotionRuleFormSamples[sampleIndex];
        if (sample) {
            methods.setValue('requiredYears', sample.requiredYears);
            methods.setValue('description', sample.description);
            methods.setValue('isActive', sample.isActive);
            setSampleIndex((prev) => (prev + 1) % promotionRuleFormSamples.length);
            toast.success('ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາເລືອກຈາກ/ໄປຕຳແໜ່ງ.');
        }
    };

    const submit = (values: FormValues) => {
        const payload = {
            fromPositionId: values.fromPositionId,
            toPositionId: values.toPositionId,
            requiredYears: values.requiredYears ?? null,
            description: values.description.trim() || null,
            isActive: values.isActive,
        };

        if (isCreate) {
            (props as Extract<Props, { mode: 'create' }>).onSubmit(payload);
            return;
        }
        (props as Extract<Props, { mode: 'edit' }>).onSubmit(payload);
    };

    return (
        <FormRoot<FormValues>
            methods={methods}
            onSubmit={submit}
            className="space-y-6"
        >
            <Card>
                <CardHeader>
                    <CardTitle>
                        {isCreate ? 'ສ້າງຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ' : 'ແກ້ໄຂຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <FormInfiniteCombobox<LookupItem>
                        name="fromPositionId"
                        label="ຈາກຕຳແໜ່ງ"
                        queryKey={['positions-lookup-rule']}
                        queryFn={(args) =>
                            fetchLookupForInfinite<LookupItem>(
                                POSITIONS_LOOKUP_URL,
                                args,
                            )
                        }
                        preloadQueryFn={(id) =>
                            hydrateLookupItem<LookupItem>(
                                POSITIONS_LOOKUP_URL,
                                id,
                            )
                        }
                        getLabel={(item) => item.name}
                        getValue={(item) => item.id}
                        placeholder="ເລືອກຕຳແໜ່ງ"
                        requiredMark
                        className="w-full"
                    />

                    <FormInfiniteCombobox<LookupItem>
                        name="toPositionId"
                        label="ໄປຕຳແໜ່ງ"
                        queryKey={['positions-lookup-rule-to']}
                        queryFn={(args) =>
                            fetchLookupForInfinite<LookupItem>(
                                POSITIONS_LOOKUP_URL,
                                args,
                            )
                        }
                        preloadQueryFn={(id) =>
                            hydrateLookupItem<LookupItem>(
                                POSITIONS_LOOKUP_URL,
                                id,
                            )
                        }
                        getLabel={(item) => item.name}
                        getValue={(item) => item.id}
                        placeholder="ເລືອກຕຳແໜ່ງ"
                        requiredMark
                        className="w-full"
                    />

                    <FormNumber
                        name="requiredYears"
                        label="ຈຳນວນປີປະສົບການ"
                        placeholder="2"
                    />
                    <FormTextarea
                        name="description"
                        label="ຄຳອະທິບາຍ"
                        rows={3}
                    />
                    <div className="flex items-center justify-between rounded-lg border p-4">
                        <div>
                            <p className="font-medium text-sm">ເປີດໃຊ້ງານ</p>
                            <p className="text-muted-foreground text-xs">
                                ຄວບຄຸມການນຳ rule ນີ້ໄປໃຊ້
                            </p>
                        </div>
                        <Switch
                            id="promotion-rule-active"
                            checked={methods.watch('isActive')}
                            onCheckedChange={(checked) =>
                                methods.setValue('isActive', checked)
                            }
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={props.onCancel}
                        disabled={props.isSubmitting}
                    >
                        ຍົກເລີກ
                    </Button>
                    {isDev && isCreate && promotionRuleFormSamples.length > 0 && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAutoFill}
                            disabled={props.isSubmitting}
                            className="gap-1.5"
                        >
                            <Sparkles className="h-4 w-4" />
                            Auto Fill (Sample {sampleIndex + 1}/{promotionRuleFormSamples.length})
                        </Button>
                    )}
                </div>
                <Button
                    type="submit"
                    isLoading={props.isSubmitting}
                    disabled={props.isSubmitting}
                >
                    {isCreate ? 'ສ້າງ' : 'ບັນທຶກ'}
                </Button>
            </div>
        </FormRoot>
    );
}
